export interface ModelVersion {
  id: string;
  createdDate: string;
  createdBy: string;
  size: string;
  versionNumber: string;
  isFilesLoading: boolean;
  files?: VersionFileResponse["versions"];
}

export interface VersionFileResponse {
  versions: {
    data: (VersionFile | VersionDirectory)[];
    total: number;
    page: number;
    size: number;
  };
}

export type VersionFile = CommonVersionFile & { type: "File" };

export type VersionDirectory = CommonVersionFile & {
  type: "Directory";
  isExpanded: boolean;
  isFilesLoading: boolean;
  files?: VersionFileResponse["versions"];
  totalFileCount: number | null;
  totalDirectoryCount: number | null;
};

interface CommonVersionFile {
  id: string;
  versionNumber: string;
  createdBy: string;
  filePath: string;
  size?: string;
  fileName: string;
  fileFormat?: FileFormats;
  totalRows: string | null;
  totalColumns?: string | null;
  isStructured?: boolean;
  parentId: string | null;
}

export enum FileFormats {
  txt = "text/plain",
  jpg = "image/jpeg",
  png = "image/png",
  pdf = "application/pdf",
  xlsx = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  mp4 = "video/mp4",
  mp3 = "audio/mpeg",
  json = "application/json",
  csv = "text/csv",
  wav = "audio/wav",
  md = "text/markdown",
}

export enum FileTypes {
  directory = "Directory",
  file = "File",
}

export interface ModelDetails {
  id: string;
  name: string;
  imageUrl: string;
  views: any;
  downloads: any;
  createdDate: string;
  shortDescription: string;
  fullDescription: string;
  isDownloadRequested?: boolean;
  isAppealedRequested?: boolean;
  isBookmarked?: boolean;
  slug: string;
  currentOwner: CurrentOwner;
  updatedAt: string;
  reviewStatus: string;
  remark: string;
  deleteRequest: boolean;
  deleteRequestRemark: any;
  tags: Tag[];
  metadata: Metadata;
  stats: Stats;
  artifactPermission: string;
  downloadApprovalRequired?: boolean;
  viewModel: boolean;
  dArtifactRequestRepo: any[];
  isEscalated?: boolean;
  viewDataset: boolean;
  editDataset: boolean;
  permissionType: string;
  userData: UserData;
  model: Model;
  rawPermissions: RawPermissions;
  model_external_url: string;
}

export interface CurrentOwner {
  id: string;
  username: string;
  name: string;
  profilepic: string;
  designation: string;
  organization: Organization;
  role: Role;
}

export interface Organization {
  id: string;
  name: string;
  logo: string;
}

export interface Role {
  id: number;
  name: string;
}

export interface Tag {
  id: number;
  tag: string;
}

export interface Metadata {
  id: string;
  modelId: string;
  author: string;
  visibility: string;
  geographicalCoverage: string;
  license: string;
  sector: string;
  modelType: string;
  uploadedBy: UploadedBy;
  sourceOrg: SourceOrg;
  startDate: any;
  endDate: any;
  updatedDate: string;
  likes: string;
  permissions: any;
  modelFormat: string;
}

export interface UploadedBy {
  id: string;
  username: string;
  name: string;
  profilepic: string;
  designation: string;
  organization: Organization2;
  role: Role2;
}

export interface Organization2 {
  id: string;
  name: string;
  logo: string;
}

export interface Role2 {
  id: number;
  name: string;
}

export interface SourceOrg {
  id: string;
  name: string;
}

export interface Stats {
  views: number;
  downloads: number;
  size: number;
}

export interface UserData {
  requestedOrganizationName: string;
  isOrganizationRequest: boolean;
  id: string;
  email: string;
  username: string;
  oauth_id: string;
  name: string;
  dateOfBirth: string;
  gender: string;
  phone: string;
  linkedIn: string;
  gitHub: string;
  facebook: string;
  twitterX: string;
  bio: string;
  designation: string;
  profile_image: string;
  countryCode: string;
  organisation: Organisation;
  roles: Role3[];
}

export interface Organisation {
  id: string;
  name: string;
  slug: any;
}

export interface Role3 {
  id: number;
  name: string;
  slug: string;
}

export enum ArtefactType {
  dataset = "dataset",
  model = "model",
  usecase = "usecase",
  toolkit = "toolkit",
}

export enum OrganizationType {
  all = "all",
  public = "public",
  private = "private",
  researchOrganization = "research_organization",
  startupMSME = "startup_msme",
  university = "university",
}

export interface Model {
  id: string;
  name: string;
  created_by: any;
  views: any;
  downloads: any;
  created_date: string;
  description: string;
  full_description: string;
  organization_id: any;
  current_owner: string;
  artifactPermissionId: any;
  slug: string;
  updated_by: string;
  artifact_permission: any;
  updated_date: string;
  image_url: string;
  review_status: string;
  reviewed_by: any;
  review_at: string;
  delete_request: boolean;
  remark: string;
  delete_request_remark: any;
  __entity: string;
}

export interface RawPermissions {
  id: string;
  approvalRequired: boolean;
  createdAt: string;
  isFree: boolean;
  organizationId: string;
  permissionType: string;
  createdBy: string;
}

export interface ModelVersionResponse {
  versions: {
    data: ModelVersion[];
    total: number;
    page: number;
    size: number;
  };
}
